/**
 * DriverNotificationsPage — `/driver-portal/notifications`.
 *
 * Live · GET /v1/driver-portal/me/notifications. Read-state is updated via
 * POST /v1/driver-portal/me/notifications/:id/read. Filter chrome is
 * client-side over the latest 100 inbox rows (server returns newest first).
 */
import { type JSX, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Filter } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getDriverPortalNotifications,
  markDriverNotificationRead,
  type DriverNotification,
} from '../../lib/api/driver-portal';
import { PageHeader } from '../shared/StaffUI';
import { useDriverPortalContext } from './DriverPortalLayout';

const CATEGORIES: readonly string[] = [
  'ALL',
  'SAFETY',
  'REASSIGNMENT',
  'ADVISORY',
  'WEATHER',
  'PAYROLL',
  'INCENTIVE',
  'TRAINING',
  'ATTESTATION',
  'RATING',
  'SYSTEM',
];

export function DriverNotificationsPage(): JSX.Element {
  const { driverId } = useDriverPortalContext();
  const qc = useQueryClient();
  const [category, setCategory] = useState<string>('ALL');
  const [showRead, setShowRead] = useState<boolean>(true);

  const inbox = useQuery({
    queryKey: ['driver-portal', 'notifications', driverId],
    queryFn: () => getDriverPortalNotifications(driverId),
    retry: false,
    staleTime: 15_000,
  });

  const denied =
    inbox.isError &&
    inbox.error instanceof ApiError &&
    (inbox.error.status === 401 || inbox.error.status === 403);

  const markRead = useMutation({
    mutationFn: (id: string) => markDriverNotificationRead(id, driverId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['driver-portal', 'notifications', driverId] });
    },
  });

  const filtered = useMemo<DriverNotification[]>(() => {
    return (inbox.data?.items ?? []).filter((n: DriverNotification) => {
      if (category !== 'ALL' && n.category !== category) return false;
      if (!showRead && n.readAt != null) return false;
      return true;
    });
  }, [inbox.data, category, showRead]);

  return (
    <div>
      <PageHeader
        title="Notifications"
        description={`Schedule changes, advisories, payroll, training, safety. Live inbox · ${inbox.data?.unread ?? 0} unread.`}
      />

      {denied ? (
        <p className="text-sm text-amber-700 mt-4">
          Permission required: <code className="font-mono">Driver/readNotifications</code>
        </p>
      ) : null}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mt-4 mb-4">
        <Filter className="w-4 h-4 text-muted" />
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md ${
                category === c
                  ? 'bg-nipost-green-light text-nipost-green'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {c.replace('_', ' ').toLowerCase()}
            </button>
          ))}
        </div>
        <label className="ml-auto inline-flex items-center gap-2 text-xs text-muted cursor-pointer">
          <input
            type="checkbox"
            checked={showRead}
            onChange={(e) => setShowRead(e.target.checked)}
            className="w-3.5 h-3.5"
            data-testid="show-read-toggle"
          />
          Show read
        </label>
      </div>

      {inbox.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center text-muted">
          <Bell className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-semibold text-gray-700">No notifications</p>
          <p className="text-xs mt-1">
            {(inbox.data?.items.length ?? 0) === 0
              ? 'Your inbox is empty.'
              : 'Nothing matches the active filter.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="notification-list">
          {filtered.map((n) => (
            <li
              key={n.id}
              className={`bg-white border border-border rounded-xl p-4 ${
                n.readAt == null ? 'border-l-4 border-l-nipost-green' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-muted font-semibold">
                    {n.category.toLowerCase()}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">{n.subject}</p>
                  <p className="text-xs text-muted mt-1 whitespace-pre-line">{n.body}</p>
                  <p className="text-[10px] text-muted mt-2">{new Date(n.createdAt).toLocaleString()}</p>
                </div>
                {n.readAt == null ? (
                  <button
                    type="button"
                    disabled={markRead.isPending}
                    onClick={() => markRead.mutate(n.id)}
                    data-testid={`mark-read-${n.id}`}
                    className="text-xs font-semibold text-nipost-green hover:underline"
                  >
                    Mark read
                  </button>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest text-success">read</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
